import { css } from "@emotion/css";
import type { BreadcrumbItem, PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository, SpaceResource, TaskResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { TaskStatusIcon } from "~/areas/projects/components/TaskStatusIcon/TaskStatusIcon";
import { client } from "~/clientInstance";
import InternalLink from "~/components/Navigation/InternalLink/InternalLink";
import { PageContent } from "~/components/PageContent/PageContent";
interface RawTaskLogPageProps {
    taskId: string;
    loaderData: RawTaskLogPageLoaderData;
}
interface RawTaskLogPageLoaderData {
    task: TaskResource<unknown>;
    taskSpace: SpaceResource | undefined;
    raw: string;
}
export async function rawTaskLogPageLoader(repository: Repository, taskId: string): Promise<RawTaskLogPageLoaderData> {
    return loadRawTaskData(repository, taskId);
}
export function RawTaskLogPage({ taskId, loaderData }: RawTaskLogPageProps) {
    const { result: rawLogData, isLoading, error, refetch } = useInlineStatusQuery(async (repository) => loadRawTaskData(repository, taskId), [taskId], "Load raw task log", { initialResult: loaderData });
    const pageActions: PageAction[] = [
        {
            type: "navigate",
            buttonType: "secondary",
            path: rawLogData.task.Links["Raw"],
            label: "Download",
            external: true,
        },
    ];
    const refreshPageAction: PrimaryPageAction = {
        type: "button",
        label: "Refresh",
        disabled: isLoading,
        onClick: refetch,
    };
    const breadcrumbsItems: BreadcrumbItem[] = [
        { label: "Tasks", pageUrl: links.tasksPage.generateUrl() },
        { label: rawLogData.task.Description, pageUrl: links.taskPage.generateUrl({ taskId: rawLogData.task.Id }) },
    ];
    return (<div className={containerStyles}>
            <PageContent header={{
            title: "Raw Log",
            logo: <TaskStatusIcon item={loaderData.task}/>,
            breadcrumbs: breadcrumbsItems,
            pageActions,
            primaryAction: refreshPageAction,
        }} busy={isLoading} errors={error ? [error] : undefined} callout={rawLogData.taskSpace && rawLogData.taskSpace.Id !== client.spaceId
            ? {
                type: "information",
                content: (<>
                                      You are viewing a task from Space: <strong>{rawLogData.taskSpace.Name}</strong>.<InternalLink to={links.spaceRootRedirect.generateUrl({ spaceId: rawLogData.taskSpace.Id })}>Change to this Space.</InternalLink>
                                  </>),
            }
            : undefined}>
                <div className={rawTaskLogContent}>{rawLogData.raw}</div>
            </PageContent>
        </div>);
}
async function loadRawTaskData(repository: Repository, taskId: string) {
    const task = await repository.Tasks.get(taskId, { spaces: ["all"] });
    const taskSpaceAsync = task.SpaceId ? repository.Spaces.get(task.SpaceId) : undefined;
    const rawAsync = repository.Tasks.getRaw(task);
    return {
        task,
        taskSpace: await taskSpaceAsync,
        raw: await rawAsync,
    };
}
export function RawTaskLogPageLoadingContent({ state }: {
    state: Error | "is-loading";
}) {
    const breadcrumbsItems: BreadcrumbItem[] = [{ label: "Tasks", pageUrl: links.tasksPage.generateUrl() }];
    return (<div className={containerStyles}>
            <PageContent header={{
            title: "Raw Log",
            breadcrumbs: breadcrumbsItems,
        }} busy={state === "is-loading"} errors={state === "is-loading" ? [] : [state]}>
                {null}
            </PageContent>
        </div>);
}
const containerStyles = css({
    margin: space[32],
});
const rawTaskLogContent = css({
    font: text.code.regular.xSmall,
    padding: space[16],
    margin: space[16],
    wordBreak: "break-all",
    wordWrap: "break-word",
    backgroundColor: themeTokens.color.background.secondary.default,
    border: `${space[1]} solid ${themeTokens.color.border.primary}`,
    borderRadius: borderRadius.extraSmall,
    whiteSpace: "pre",
    overflowX: "scroll",
    textRendering: "optimizeSpeed",
});
